import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Plus, Trash2, X, Receipt, Minus, Paperclip, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

const CATEGORIES = ["Diğer ödeme", "Nakit avans", "Personel ödemesi", "Vergi/SGK", "Kira", "Fatura (elektrik/su/gaz)", "Yakıt", "Yemek", "Konaklama", "Ulaşım", "Kırtasiye", "Temizlik", "Bakım-Onarım"];

export default function DailyExpensesPage({ company, companies, user }) {
  const canEdit = user?.role !== "viewer";
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({
    start: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });
  const [showForm, setShowForm] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/daily-expenses`, { params: { company_id: company, start: range.start, end: range.end } });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company, range]);
  const del = async (r) => {
    if (!window.confirm("Silinsin mi?")) return;
    await axios.delete(`${API}/daily-expenses/${r.id}`); load();
  };
  const totalTry = rows.reduce((s, r) => s + (r.try_amount || 0), 0);
  const byDay = rows.reduce((acc, r) => { acc[r.expense_date] = (acc[r.expense_date] || 0) + (r.try_amount || 0); return acc; }, {});

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">GÜNLÜK ÖDEMELER</p>
        <h2>{rows.length} Kayıt · {money(totalTry)}</h2>
        <p className="muted">O gün yapılması planlanan diğer ödemeler. Çek, senet, kredi taksitleri ve kredi kartı ödemeleri kendi modüllerinde takip edilir — burada sadece <strong>onların dışındaki</strong> ödemeler girilir. Nakit akış tablosuna otomatik yansır.</p>
      </div>
      <div className="toolbar-actions" style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <div style={{ display: "flex", gap: 6 }}>
          <input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} data-testid="exp-range-start" style={{ padding: "9px 12px", borderRadius: 6, border: "1px solid var(--input-border)", background: "var(--input-bg)", color: "var(--text)", fontSize: 13 }} />
          <input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} data-testid="exp-range-end" style={{ padding: "9px 12px", borderRadius: 6, border: "1px solid var(--input-border)", background: "var(--input-bg)", color: "var(--text)", fontSize: 13 }} />
        </div>
        {canEdit && <button className="primary-btn" data-testid="exp-new-btn" onClick={() => setShowForm(true)}><Plus size={17} /> Ödeme gir</button>}
      </div>
    </section>

    <section className="metric-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", marginBottom: 16 }}>
      {Object.entries(byDay).sort(([a], [b]) => b.localeCompare(a)).slice(0, 7).map(([d, total]) => (
        <div key={d} className="metric-card" style={{ padding: 12 }}>
          <div className="metric-label" style={{ fontSize: 11 }}>{d}</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{money(total)}</div>
        </div>
      ))}
    </section>

    <section className="panel full-panel">
      <div className="table-scroll"><table>
        <thead><tr>
          <th>TARİH</th><th>TİP</th><th>KURUM/KİŞİ</th><th>KATEGORİ</th><th>AÇIKLAMA</th>
          <th>TUTAR</th><th>TL</th><th>ŞİRKET</th><th></th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="9" style={{ textAlign: "center", padding: 24 }} className="muted">Seçilen tarih aralığında kayıt yok.</td></tr>}
          {rows.map(r => <tr key={r.id} data-testid={`exp-row-${r.id}`}>
            <td>{r.expense_date}</td>
            <td><span className="company-pill" style={{ background: r.owner_type === "corporate" ? "#dbeafe" : "#fef3c7", color: r.owner_type === "corporate" ? "#1e40af" : "#92400e" }}>
              {r.owner_type === "corporate" ? "Tüzel" : "Şahıs"}
            </span></td>
            <td><strong>{r.owner_name}</strong></td>
            <td>{r.category}</td>
            <td>{r.description || "—"}</td>
            <td>{money(r.amount, r.currency)}</td>
            <td>{money(r.try_amount)}</td>
            <td><span className="company-pill">{(r.company_id || "").toUpperCase()}</span></td>
            <td style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {(r.attachment_ids || []).length > 0 && (
                <button type="button" className="icon-btn" title={`${r.attachment_ids.length} ek`}
                        data-testid={`exp-attach-view-${r.id}`}
                        onClick={async () => {
                          try {
                            const resp = await axios.get(`${API}/files/${r.attachment_ids[0]}/download`, { responseType: "blob" });
                            const url = URL.createObjectURL(resp.data);
                            window.open(url, "_blank", "noopener");
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (ex) {
                            alert(ex.response?.data?.detail || "Dosya açılamadı");
                          }
                        }}><Paperclip size={13} /></button>
              )}
              {canEdit && <button className="icon-btn" onClick={() => del(r)} data-testid={`exp-del-${r.id}`}><Trash2 size={13} /></button>}
            </td>
          </tr>)}
        </tbody>
      </table></div>
    </section>

    {showForm && <ExpenseForm companies={companies} onClose={() => setShowForm(false)} onDone={(submittedDate) => {
      // Auto-widen range if submitted date falls outside current window
      if (submittedDate) {
        setRange(prev => ({
          start: submittedDate < prev.start ? submittedDate : prev.start,
          end: submittedDate > prev.end ? submittedDate : prev.end,
        }));
      }
      setShowForm(false); load();
    }} />}
  </div>;
}

function ExpenseForm({ companies, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [ownerType, setOwnerType] = useState("corporate");
  const emptyRow = { owner_name: "", amount: "", currency: "TRY", description: "", category: "Diğer ödeme", attachment_ids: [] };
  const [header, setHeader] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    company_id: companies?.[0]?.id || "",
  });
  const [items, setItems] = useState([{ ...emptyRow }]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const addRow = () => setItems([...items, { ...emptyRow }]);
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updRow = (i, k, v) => setItems(items.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const attachToRow = async (i, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_type", "daily_expense");
      if (header.company_id) fd.append("company_id", header.company_id);
      const { data } = await axios.post(`${API}/files/upload`, fd);
      const row = items[i];
      updRow(i, "attachment_ids", [...(row.attachment_ids || []), data.id]);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Dosya yüklenemedi");
    }
  };
  const removeAttach = (i, fid) => {
    const row = items[i];
    updRow(i, "attachment_ids", (row.attachment_ids || []).filter(x => x !== fid));
  };

  // "Personel ödemesi" (maaş vb.) ve "Vergi/SGK" seçildiğinde kurum/kişi adı opsiyoneldir.
  const ownerOptional = (cat) => ["Personel ödemesi", "Vergi/SGK"].includes(cat);
  const ownerPlaceholder = (cat) => {
    if (cat === "Personel ödemesi") return "Kişi/pozisyon (opsiyonel)";
    if (cat === "Vergi/SGK") return "Kurum (opsiyonel)";
    return ownerType === "corporate" ? "Kurum adı" : "Kişi adı";
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = items.filter(it => it.amount && (ownerOptional(it.category) || it.owner_name));
    if (clean.length === 0) { setErr("En az bir ödeme girin"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/daily-expenses`, {
        owner_type: ownerType,
        expense_date: header.expense_date,
        company_id: header.company_id,
        items: clean.map(it => ({
          ...it,
          owner_name: it.owner_name || (it.category === "Vergi/SGK" ? "Vergi/SGK" : it.category === "Personel ödemesi" ? "Personel ödemesi" : ""),
          amount: parseFloat(it.amount),
        })),
      });
      onDone(header.expense_date);
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  if (step === 1) {
    return <Modal onClose={onClose} title="Ödeme tipi seçin">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: 20 }}>
        <button type="button" data-testid="exp-type-corporate" onClick={() => { setOwnerType("corporate"); setStep(2); }}
          style={{ padding: 24, borderRadius: 10, border: "2px solid #e5e7eb", background: "white", color: "#0f172a", cursor: "pointer", textAlign: "left" }}>
          <Receipt size={32} color="#1e40af" />
          <h4 style={{ margin: "12px 0 4px", color: "#0f172a" }}>Kurumsal</h4>
          <p style={{ fontSize: 13, color: "#475569", margin: 0 }}>Şirket adına yapılacak operasyonel ödemeler</p>
        </button>
        <button type="button" data-testid="exp-type-personal" onClick={() => { setOwnerType("personal"); setStep(2); }}
          style={{ padding: 24, borderRadius: 10, border: "2px solid #e5e7eb", background: "white", color: "#0f172a", cursor: "pointer", textAlign: "left" }}>
          <Receipt size={32} color="#f59e0b" />
          <h4 style={{ margin: "12px 0 4px", color: "#0f172a" }}>Şahsi</h4>
          <p style={{ fontSize: 13, color: "#475569", margin: 0 }}>Kişi adına yapılan ödemeler (çalışan avansları vs.)</p>
        </button>
      </div>
    </Modal>;
  }

  return <Modal onClose={onClose} wide title={`Yeni ${ownerType === "corporate" ? "kurumsal" : "şahsi"} ödeme`}>
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Tarih"><input data-testid="exp-form-date" type="date" value={header.expense_date} onChange={e => setHeader({ ...header, expense_date: e.target.value })} required /></Field>
        <Field label="Şirket"><select data-testid="exp-form-company" value={header.company_id} onChange={e => setHeader({ ...header, company_id: e.target.value })}>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select></Field>
      </div>
      <div style={{ padding: "8px 0" }}><strong>Ödeme kalemleri</strong></div>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} data-testid={`exp-row-form-${i}`} style={{ padding: 6, borderRadius: 8, border: "1px dashed var(--border, #e5e7eb)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 90px 2fr 1fr auto", gap: 6, alignItems: "center" }}>
              <input placeholder={ownerPlaceholder(it.category)} value={it.owner_name} onChange={e => updRow(i, "owner_name", e.target.value)} required={!ownerOptional(it.category)} data-testid={`exp-item-owner-${i}`} />
              <input type="number" step="0.01" placeholder="Tutar" value={it.amount} onChange={e => updRow(i, "amount", e.target.value)} required data-testid={`exp-item-amount-${i}`} />
              <select value={it.currency} onChange={e => updRow(i, "currency", e.target.value)} data-testid={`exp-item-currency-${i}`}>
                {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
              </select>
              <input placeholder="Açıklama (ops.)" value={it.description} onChange={e => updRow(i, "description", e.target.value)} data-testid={`exp-item-desc-${i}`} />
              <select value={it.category} onChange={e => updRow(i, "category", e.target.value)} data-testid={`exp-item-cat-${i}`}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <button type="button" className="icon-btn" onClick={() => removeRow(i)} disabled={items.length === 1} data-testid={`exp-item-remove-${i}`}><Minus size={13} /></button>
            </div>
            <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12 }}>
              <label className="outline-btn" style={{ padding: "4px 8px", fontSize: 11, cursor: "pointer" }} data-testid={`exp-item-attach-${i}`}>
                <Paperclip size={12} /> Fiş/Fatura ekle
                <input type="file" style={{ display: "none" }} accept="image/*,application/pdf"
                  onChange={e => { attachToRow(i, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {(it.attachment_ids || []).map(fid => (
                <span key={fid} data-testid={`exp-attach-chip-${i}-${fid}`} style={{ padding: "2px 8px", background: "#dbeafe", color: "#1e40af", borderRadius: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <FileText size={11} /> fiş
                  <button type="button" onClick={() => removeAttach(i, fid)} style={{ border: 0, background: "transparent", color: "#1e40af", cursor: "pointer", padding: 0 }}><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="outline-btn" onClick={addRow} style={{ marginTop: 10 }} data-testid="exp-item-add"><Plus size={14} /> Satır ekle</button>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={() => setStep(1)}>Geri</button>
        <button data-testid="exp-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : `${items.filter(it => it.amount && (ownerOptional(it.category) || it.owner_name)).length} ödeme kaydet`}</button>
      </div>
    </form>
  </Modal>;
}
